import { useCallback, useEffect, useRef, useState } from 'react'
import { messagesApi } from '../api/messages'
import { ApiError } from '../api/client'
import { useChatStore } from '../stores/chatStore'
import { messageCache } from '../lib/messageCache'
import type { Message } from '../types'

// Стабильный пустой массив на уровне модуля — решает React #185:
// `s.messages[chatId] ?? []` каждый вызов возвращает новый [], React 18's
// useSyncExternalStore делает Object.is(prev,[]) === false → forceStoreRerender
// → бесконечный цикл. Константный массив всегда === самому себе.
const EMPTY_MESSAGES: Message[] = []

export function useMessages(chatId: string | null) {
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const initialized = useRef<Set<string>>(new Set())
  // True until load() has been called for the current chatId.
  // Prevents MessageList from computing scroll position before the first
  // fetch starts (which would pin Virtuoso at index 0 on page refresh or
  // chat switch when messages haven't populated yet).
  const isPreLoad = chatId ? !initialized.current.has(chatId) : false

  // Используем селекторы — экшены в Zustand стабильные ссылки (не пересоздаются),
  // поэтому load/loadMore не зависят от всего стора и не вызывают бесконечный цикл.
  const messages       = useChatStore((s) => (chatId ? (s.messages[chatId] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES))
  const cursor         = useChatStore((s) => (chatId ? s.nextCursor[chatId] : null))
  const setMessages    = useChatStore((s) => s.setMessages)
  const prependMessages = useChatStore((s) => s.prependMessages)
  const setNextCursor  = useChatStore((s) => s.setNextCursor)

  const load = useCallback(async (id: string) => {
    if (initialized.current.has(id)) return
    initialized.current.add(id)
    setLoading(true)

    // 1. Показываем кешированные сообщения сразу
    const cached = await messageCache.getMessages(id)
    if (cached.length > 0) {
      setMessages(id, cached.sort((a, b) => a.created_at.localeCompare(b.created_at)))
    }

    try {
      const latestAt = await messageCache.getLatestMessageAt(id)

      if (latestAt && cached.length > 0) {
        // 2a. Delta sync — только новые сообщения поверх кеша
        const delta = await messagesApi.getHistoryAfter(id, latestAt)
        const newMsgs = delta.messages ?? []
        if (newMsgs.length > 0) {
          const merged = [...cached, ...newMsgs].sort((a, b) =>
            a.created_at.localeCompare(b.created_at),
          )
          setMessages(id, merged)
          await messageCache.mergeMessages(id, newMsgs)
        }
        const oldest = cached[0]?.created_at
        setNextCursor(id, oldest ?? null)
      } else {
        // 2b. Cold start — грузим первую страницу
        const res = await messagesApi.getHistory(id)
        const msgs = (res.messages ?? []).reverse()
        setMessages(id, msgs)
        setNextCursor(id, res.next_cursor || null)
        await messageCache.saveMessages(id, msgs)
      }
    } catch (e) {
      // Чат удалён на сервере (404) или мы больше не участник (403) —
      // убираем его из списка и кэша, чтобы не висел «мёртвым».
      if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
        useChatStore.getState().removeChat(id)
        await messageCache.deleteChat(id)
      }
      // Иначе сетевая ошибка — кеш уже показан, оставляем его
    } finally {
      setLoading(false)
    }
  }, [setMessages, setNextCursor])

  const loadMore = useCallback(async () => {
    if (!chatId || !cursor || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await messagesApi.getHistory(chatId, cursor)
      const older = (res.messages ?? []).reverse()
      prependMessages(chatId, older)
      setNextCursor(chatId, res.next_cursor || null)
      // Не сохраняем в кеш: кеш хранит только последние сообщения,
      // старые при пагинации грузятся с сети на лету.
    } finally {
      setLoadingMore(false)
    }
  }, [chatId, cursor, loadingMore, prependMessages, setNextCursor])

  useEffect(() => {
    if (chatId) load(chatId)
  }, [chatId, load])

  const jumpToDate = useCallback(async (date: Date) => {
    if (!chatId) return
    const beforeDate = new Date(date)
    beforeDate.setDate(beforeDate.getDate() + 1)
    const before = beforeDate.toISOString()
    setLoading(true)
    try {
      const res = await messagesApi.getHistory(chatId, before)
      setMessages(chatId, (res.messages ?? []).reverse())
      setNextCursor(chatId, res.next_cursor || null)
      initialized.current.delete(chatId)
      initialized.current.add(chatId)
    } finally {
      setLoading(false)
    }
  }, [chatId, setMessages, setNextCursor])

  return { messages, loading: loading || isPreLoad, loadingMore, loadMore, hasMore: !!cursor, jumpToDate }
}
